import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {DigitalVosForm} from './DigitalVosForm'
import {buildDigitalVosFormData} from './buildDigitalVosFormData'
import type {BuildDigitalVosFormInput} from './digitalVosFormTypes'
import {exportDigitalVosPdf, waitForImages} from './exportDigitalVosPdf'
import {resolveVosSignatureUrls, revokeVosSignatureDisplayUrls} from './resolveVosSignatureUrls'
import type {DigitalVosSignatureUrls} from './digitalVosFormTypes'
import {
  fetchAppointmentCheckInAudit,
  hasCheckInStaffAudit,
  mergeCheckInAuditFromAppointment,
} from './fetchAppointmentCheckInAudit'
import {
  isReactNativeWebView,
  postPdfErrorToReactNative,
} from './reactNativePdfBridge'

export type DigitalVosFormPanelProps = {
  input: BuildDigitalVosFormInput
  fileName: string
  /** Renders modal-style header with Close + Download (standalone page / modal). */
  standalone?: boolean
  onClose?: () => void
  onDownloadError?: (message: string) => void
  onDownloadingChange?: (downloading: boolean) => void
  onLoadingSignaturesChange?: (loading: boolean) => void
}

export type DigitalVosFormPanelHandle = {
  downloadPdf: () => Promise<void>
}

export const DigitalVosFormPanel = forwardRef<DigitalVosFormPanelHandle, DigitalVosFormPanelProps>(
  function DigitalVosFormPanel(
    {
      input,
      fileName,
      standalone = false,
      onDownloadError,
      onDownloadingChange,
      onLoadingSignaturesChange,
    },
    ref
  ) {
    const rootRef = useRef<HTMLDivElement>(null)
    const signaturesRef = useRef<DigitalVosSignatureUrls>({})
    const [signatures, setSignatures] = useState<DigitalVosSignatureUrls>({})
    const [loadingSignatures, setLoadingSignatures] = useState(false)
    const [downloadingPdf, setDownloadingPdf] = useState(false)

    const [appointmentForForm, setAppointmentForForm] = useState<Record<string, unknown>>(
      () => (input.appointment ?? {}) as Record<string, unknown>
    )

    useEffect(() => {
      setAppointmentForForm((input.appointment ?? {}) as Record<string, unknown>)
    }, [input.appointment])

    const formInput = useMemo(
      () => ({
        ...input,
        appointment: appointmentForForm,
      }),
      [input, appointmentForForm]
    )

    const formData = useMemo(() => buildDigitalVosFormData(formInput), [formInput])

    useEffect(() => {
      let cancelled = false
      const appointmentId =
        (input.appointment?.code as string | number) ??
        (input.appointment?.Code as string | number) ??
        null

      const loadAuditAndSignatures = async () => {
        setLoadingSignatures(true)
        onLoadingSignaturesChange?.(true)
        let appt = (input.appointment ?? {}) as Record<string, unknown>

        if (appointmentId && !hasCheckInStaffAudit(appt)) {
          try {
            const fetched = await fetchAppointmentCheckInAudit(appointmentId)
            if (!cancelled) {
              appt = mergeCheckInAuditFromAppointment(appt, fetched)
              setAppointmentForForm(appt)
            }
          } catch (e) {
            console.error('Failed to load check-in staff for VOS form', e)
          }
        }

        const summary = input.meetingSummary ?? {}
        return resolveVosSignatureUrls({
          checkInStaffSignUrl:
            (appt.checkInStaffSignUrl as string) ?? (appt.CheckInStaffSignUrl as string),
          reqSignUrl: (summary.reqSignUrl as string) ?? (summary.ReqSignUrl as string),
          bulkApproverSignUrl:
            (summary.bulkApproverSignUrl as string) ?? (summary.BulkApproverSignUrl as string),
          terpSignUrl: (summary.terpSignUrl as string) ?? (summary.TerpSignUrl as string),
        })
      }

      loadAuditAndSignatures()
        .then((resolved) => {
          if (cancelled) {
            revokeVosSignatureDisplayUrls(resolved)
            return
          }
          revokeVosSignatureDisplayUrls(signaturesRef.current)
          signaturesRef.current = resolved
          setSignatures(resolved)
        })
        .catch((e) => console.error(e))
        .finally(() => {
          if (!cancelled) {
            setLoadingSignatures(false)
            onLoadingSignaturesChange?.(false)
          }
        })

      return () => {
        cancelled = true
      }
    }, [input.appointment, input.meetingSummary, onLoadingSignaturesChange])

    useEffect(() => {
      return () => {
        revokeVosSignatureDisplayUrls(signaturesRef.current)
      }
    }, [])

    const reportDownloadError = useCallback(
      (message: string) => {
        if (isReactNativeWebView()) {
          postPdfErrorToReactNative(message)
        }
        onDownloadError?.(message)
      },
      [onDownloadError]
    )

    const handleDownloadPdf = useCallback(async () => {
      const root = rootRef.current?.querySelector('#digital-vos-form-root') as HTMLElement | null
      if (!root) {
        reportDownloadError('Form is not ready for export.')
        return
      }
      setDownloadingPdf(true)
      onDownloadingChange?.(true)
      try {
        await waitForImages(root)
        await exportDigitalVosPdf(root, fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`)
      } catch (e) {
        console.error(e)
        reportDownloadError('Failed to generate VOS PDF. Please try again.')
      } finally {
        setDownloadingPdf(false)
        onDownloadingChange?.(false)
      }
    }, [fileName, onDownloadingChange, reportDownloadError])

    useImperativeHandle(ref, () => ({downloadPdf: handleDownloadPdf}), [handleDownloadPdf])

    const header = standalone ? (
      <div className='modal-header border-0 flex-shrink-0 px-6 py-4'>
        <h5 className='modal-title fw-semibold'>Digital VOS Form</h5>
        <div className='d-flex align-items-center gap-3 ms-auto'>
          <button
            type='button'
            className='btn btn-primary'
            disabled={loadingSignatures || downloadingPdf}
            onClick={handleDownloadPdf}
          >
            {downloadingPdf ? 'Generating PDF…' : 'Download PDF'}
          </button>
        </div>
      </div>
    ) : null

    const body = (
      <div
        ref={rootRef}
        className={standalone ? 'digital-vos-standalone-body flex-grow-1' : 'modal-body p-0'}
        style={
          standalone
            ? {overflow: 'auto'}
            : {overflow: 'auto', maxHeight: 'calc(100vh - 120px)'}
        }
      >
        {loadingSignatures ? (
          <div className='text-center py-10'>
            <span className='spinner-border text-primary' role='status' />
            <p className='text-muted mt-3 mb-0'>Loading signatures…</p>
          </div>
        ) : (
          <DigitalVosForm data={formData} signatures={signatures} />
        )}
      </div>
    )

    if (standalone) {
      return (
        <div className='digital-vos-standalone d-flex flex-column min-vh-100 bg-white'>
          <style>{`
            .digital-vos-standalone .digital-vos-standalone-body {
              background: #d6d6d6;
            }
            .digital-vos-standalone .digital-vos-wrapper {
              padding: 24px;
            }
          `}</style>
          {header}
          {body}
        </div>
      )
    }

    return (
      <>
        {body}
      </>
    )
  }
)
